import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowRight, Globe, Shield, Zap, TrendingDown } from 'lucide-react';
import GlobalCurrencyBanner from '@/components/GlobalCurrencyBanner';
import QuickSendWidget from '@/components/QuickSendWidget';

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted">
      <nav className="border-b border-border/50 bg-white/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/ezy-logo.png" alt="Ezy" className="h-10 w-10" />
            <span className="font-heading font-black text-2xl tracking-tighter text-primary">Transfer</span>
          </div>
          <div className="hidden md:flex items-center gap-6 mr-6">
            <Link to="/about" className="text-sm font-medium hover:text-accent transition-colors">About</Link>
          </div>
          <div className="flex gap-3">
            <Button data-testid="landing-login-btn" onClick={() => navigate('/login')} variant="outline">Login</Button>
            <Button data-testid="landing-get-started-btn" onClick={() => navigate('/register')} variant="accent" style={{backgroundColor: '#22c55e', color: '#ffffff'}}>Get Started</Button>
          </div>
        </div>
      </nav>

      {/* Global Settlement Network — live FX & crypto ticker */}
      <GlobalCurrencyBanner />

      <section className="py-20 md:py-28 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl font-black tracking-tighter text-primary mb-6">
                Send Money
                <br />
                <span className="text-accent">Globally</span> in Seconds
              </h1>
              <p className="text-base md:text-lg text-muted-foreground mb-8 leading-relaxed">
                Fast, secure, and affordable international money transfers. Send money to loved ones worldwide with transparent fees and real-time exchange rates.
              </p>
              <div className="flex gap-4">
                <Button data-testid="hero-get-started-btn" onClick={() => navigate('/register')} size="lg" variant="accent" className="h-11 px-8 gap-2" style={{backgroundColor: '#22c55e', color: '#ffffff'}}>
                  Get Started <ArrowRight className="w-4 h-4" />
                </Button>
                <Button data-testid="hero-learn-more-btn" onClick={() => navigate('/login')} size="lg" variant="outline" className="h-11 px-8">
                  Sign In
                </Button>
              </div>
            </div>
            <div className="relative">
              <QuickSendWidget />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">Why Choose EzyTransfer?</h2>
            <p className="text-base md:text-lg text-muted-foreground">Fast, secure, and transparent money transfers</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            <Card data-testid="feature-card-global" className="p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <Globe className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-2xl font-semibold mb-2">Global Reach</h3>
              <p className="text-muted-foreground">Send money to over 200 countries worldwide</p>
            </Card>
            
            <Card data-testid="feature-card-secure" className="p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <Shield className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-2xl font-semibold mb-2">Bank-Level Security</h3>
              <p className="text-muted-foreground">Your money and data are always protected</p>
            </Card>
            
            <Card data-testid="feature-card-fast" className="p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <Zap className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-2xl font-semibold mb-2">Lightning Fast</h3>
              <p className="text-muted-foreground">Most transfers complete within minutes</p>
            </Card>
            
            <Card data-testid="feature-card-low-fees" className="p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <TrendingDown className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-2xl font-semibold mb-2">Low Fees</h3>
              <p className="text-muted-foreground">Only 1% fee with transparent pricing</p>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 md:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">Ready to Send Money?</h2>
          <p className="text-base md:text-lg text-muted-foreground mb-8">
            Join thousands of users who trust EzyTransfer for their international money transfers
          </p>
          <Button data-testid="cta-get-started-btn" onClick={() => navigate('/register')} size="lg" variant="accent" className="h-11 px-8 gap-2" style={{backgroundColor: '#22c55e', color: '#ffffff'}}>
            Get Started Now <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border/50 py-12 px-6 md:px-12 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/ezy-logo.png" alt="Ezy" className="h-8 w-8" />
                <span className="font-heading font-bold text-xl text-primary">Transfer</span>
              </div>
              <p className="text-sm text-muted-foreground">
                A service by Bullaale AS. Revolutionizing money transfer through compliant, secure, and scalable digital solutions.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-muted-foreground hover:text-accent transition-colors">About Us</Link></li>
                <li><Link to="/register" className="text-muted-foreground hover:text-accent transition-colors">Get Started</Link></li>
                <li><Link to="/login" className="text-muted-foreground hover:text-accent transition-colors">Sign In</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms" className="text-muted-foreground hover:text-accent transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-muted-foreground hover:text-accent transition-colors">Privacy Policy</Link></li>
                <li><Link to="/cookies" className="text-muted-foreground hover:text-accent transition-colors">Cookie Policy</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>support@ezytransfer.com</li>
                <li>www.ezytransfer.com</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Bullaale AS & EzyTransfer. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/terms" className="text-muted-foreground hover:text-accent transition-colors">Terms</Link>
              <Link to="/privacy" className="text-muted-foreground hover:text-accent transition-colors">Privacy</Link>
              <Link to="/cookies" className="text-muted-foreground hover:text-accent transition-colors">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}